import { Injectable, inject, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { PortalLayoutResolveService } from 'src/app/admin/ad-admin/ad-layout/portal-layout-resolve.service';
import { TfService } from 'src/app/tf/tf-service.service';
import { ProductCardDisplayConfig, ProductCardLayoutType } from './product-card-display.model';
import {
  mapProductCardLayoutsRootToDisplayConfigs,
  normalizeProductCardLayoutsRoot,
} from './product-card-display.utils';

@Injectable({
  providedIn: 'root',
})
export class ProductCardDisplayConfigService {
  private readonly portalLayoutResolve = inject(PortalLayoutResolveService);
  private readonly injector = inject(Injector);

  private readonly displayConfigsSubject = new BehaviorSubject<
    Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>>
  >({});
  readonly displayConfigs$ = this.displayConfigsSubject.asObservable();

  private loaded = false;
  private loadInFlight: Observable<Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>>> | null =
    null;

  constructor() {
    this.portalLayoutResolve.loaded$.subscribe((loaded) => {
      if (!loaded) {
        this.clearCache();
        return;
      }
      this.syncFromPortalCache();
    });
  }

  /** Loads resolved product card layouts; re-syncs when portal resolve-batch completes. */
  ensureLoaded(): void {
    const hasPortalEntry = !!this.portalLayoutResolve.getEntry('product-cards');
    if (this.loaded && hasPortalEntry) {
      return;
    }
    if (this.loaded && !hasPortalEntry && this.isAuthenticated()) {
      this.loaded = false;
      this.loadInFlight = null;
    }
    if (this.loaded) {
      return;
    }
    this.load().subscribe();
  }

  watchDisplayConfig(cardType: ProductCardLayoutType): Observable<ProductCardDisplayConfig | null> {
    return this.displayConfigs$.pipe(map((configs) => configs[cardType] ?? null));
  }

  /** Refetches layouts after admin saves or assignment changes. */
  reload(): Observable<Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>>> {
    this.loaded = false;
    this.loadInFlight = null;
    return this.portalLayoutResolve.reloadSession(['product-cards']).pipe(switchMap(() => this.load()));
  }

  getSnapshot(cardType: ProductCardLayoutType): ProductCardDisplayConfig | null {
    return this.displayConfigsSubject.value[cardType] ?? null;
  }

  /** Reset cached card configs (also triggered when portal layout cache clears). */
  clearCache(): void {
    this.loaded = false;
    this.loadInFlight = null;
    this.displayConfigsSubject.next({});
  }

  /** Apply product-cards entry from portal layout cache when resolve-batch / login bootstrap completes. */
  syncFromPortalCache(): void {
    const entry = this.portalLayoutResolve.getEntry('product-cards');
    if (entry) {
      const configs = mapProductCardLayoutsRootToDisplayConfigs(
        normalizeProductCardLayoutsRoot(entry.payload),
      );
      this.displayConfigsSubject.next(configs);
      this.loaded = true;
      this.loadInFlight = null;
      return;
    }

    // Guest /home: no batch yet — use default card HTML and stop retrying.
    if (!this.isAuthenticated()) {
      this.displayConfigsSubject.next({});
      this.loaded = true;
      this.loadInFlight = null;
    }
  }

  private load(): Observable<Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>>> {
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.loadFromResolvedLayout().pipe(shareReplay(1));
    return this.loadInFlight;
  }

  private loadFromResolvedLayout(): Observable<
    Partial<Record<ProductCardLayoutType, ProductCardDisplayConfig | null>>
  > {
    const cached = this.portalLayoutResolve.getEntry('product-cards');
    if (cached) {
      const configs = mapProductCardLayoutsRootToDisplayConfigs(
        normalizeProductCardLayoutsRoot(cached.payload),
      );
      return of(configs).pipe(
        tap((resolved) => {
          this.displayConfigsSubject.next(resolved);
          this.loaded = true;
        }),
      );
    }

    return of({}).pipe(
      tap(() => {
        this.syncFromPortalCache();
        if (!this.loaded) {
          this.loadInFlight = null;
        }
      }),
    );
  }

  /** Lazy resolve avoids circular DI with TfService (which clears this cache on logout). */
  private isAuthenticated(): boolean {
    return this.injector.get(TfService).getCurrentAuthInfo().isAuthenticated;
  }
}
